<template>
  <div class="driver-management-mobile">
    <!-- 顶部统计 -->
    <div class="stats-section">
      <div class="stats-header">
        <h2>{{ $t('wholesaler.DriverManagementPage.line6_text') }}</h2>
        <span class="date">{{ currentDate }}</span>
      </div>
      <div class="stats-grid" v-if="stats">
        <div class="stat-card">
          <div class="stat-value primary">{{ stats.total || 0 }}</div>
          <div class="stat-label">{{ $t('wholesaler.DriverManagementPage.line12_text') }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-value success">{{ stats.idle || 0 }}</div>
          <div class="stat-label">{{ $t('wholesaler.DriverManagementPage.line16_text') }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-value warning">{{ stats.delivering || 0 }}</div>
          <div class="stat-label">{{ $t('wholesaler.DriverManagementPage.line20_text') }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-value info">{{ (stats.total || 0) - (stats.idle || 0) - (stats.delivering || 0) }}</div>
          <div class="stat-label">{{ $t('wholesaler.DriverManagementPage.line24_text') }}</div>
        </div>
      </div>
    </div>

    <!-- 搜索栏 -->
    <div class="search-section">
      <div class="search-bar">
        <el-input
          v-model="searchKeyword"
          :placeholder="$t('wholesaler.DriverManagementPage.line34_placeholder')"
          prefix-icon="el-icon-search"
          clearable
          @clear="loadDrivers"
          @keyup.enter.native="loadDrivers"
        >
          <el-button slot="append" type="primary" icon="el-icon-plus" @click="showAddDialog">{{ $t('wholesaler.DriverManagementPage.line40_text') }}</el-button>
        </el-input>
      </div>
      <div class="filter-bar">
        <el-select v-model="filterStatus" :placeholder="$t('wholesaler.DriverManagementPage.line44_placeholder')" clearable @change="loadDrivers" class="filter-select">
          <el-option label="空闲" value="IDLE" />
          <el-option label="配送中" value="DELIVERING" />
          <el-option label="休息" value="REST" />
          <el-option label="离线" value="OFFLINE" />
        </el-select>
      </div>
    </div>

    <!-- 配送员列表 -->
    <div class="driver-list">
      <div v-if="loading" class="loading-state">
        <div class="loading-icon">⏳</div>
        <p>{{ $t('common.loading') }}</p>
      </div>

      <div v-else-if="drivers.length === 0" class="empty-state">
        <div class="empty-icon">👨‍✈️</div>
        <p>{{ $t('wholesaler.DriverManagementPage.line62_text') }}</p>
        <el-button type="primary" @click="showAddDialog">{{ $t('wholesaler.DriverManagementPage.line63_text') }}</el-button>
      </div>

      <div v-else>
        <div v-for="driver in drivers" :key="driver.id" class="driver-card">
          <div class="driver-header">
            <div class="driver-avatar">{{ driver.name.charAt(0) }}</div>
            <div class="driver-info">
              <div class="driver-name">{{ driver.name }}</div>
              <div class="driver-phone">📞 {{ driver.phone }}</div>
              <div class="driver-vehicle" v-if="driver.vehicleType">{{ driver.vehicleType }} · {{ driver.maxLoad }}kg</div>
            </div>
            <el-tag :type="getStatusType(driver.status)" size="small" effect="plain">{{ getStatusText(driver.status) }}</el-tag>
          </div>
          
          <div class="driver-stats">
            <div class="driver-stat">
              <div class="stat-value">{{ driver.todayOrders || 0 }}</div>
              <div class="stat-label">{{ $t('wholesaler.DriverManagementPage.line81_text') }}</div>
            </div>
            <div class="driver-stat">
              <div class="stat-value">¥{{ driver.todayRevenue || 0 }}</div>
              <div class="stat-label">{{ $t('wholesaler.DriverManagementPage.line85_text') }}</div>
            </div>
            <div class="driver-stat">
              <div class="stat-value">{{ driver.completedOrders || 0 }}</div>
              <div class="stat-label">{{ $t('wholesaler.DriverManagementPage.line89_text') }}</div>
            </div>
          </div>
          
          <div class="driver-actions">
            <el-button size="small" @click="editDriver(driver)">{{ $t('common.edit') }}</el-button>
            <el-button size="small" type="primary" @click="assignOrder(driver)">{{ $t('wholesaler.DriverManagementPage.line95_text') }}</el-button>
            <el-button 
              size="small" 
              :type="driver.status === 'IDLE' ? 'warning' : 'success'"
              @click="toggleDriverStatus(driver)"
            >
              {{ driver.status === 'IDLE' ? '下班' : '上班' }}
            </el-button>
          </div>
        </div>
      </div>
    </div>

    <!-- 添加/编辑弹窗 -->
    <el-dialog 
      :title="dialogMode === 'add' ? '添加配送员' : '编辑配送员'" 
      :visible.sync="dialogVisible" 
      :close-on-click-modal="false"
      append-to-body
      custom-class="driver-dialog">
      <el-form ref="driverForm" :model="driverForm" :rules="formRules" label-width="80px">
        <el-form-item label="姓名" prop="name">
          <el-input v-model="driverForm.name" :placeholder="$t('wholesaler.DriverManagementPage.line117_placeholder')" />
        </el-form-item>
        <el-form-item label="电话" prop="phone">
          <el-input v-model="driverForm.phone" :placeholder="$t('wholesaler.DriverManagementPage.line120_placeholder')" maxlength="11" />
        </el-form-item>
        <el-form-item label="身份证" prop="idCard">
          <el-input v-model="driverForm.idCard" :placeholder="$t('wholesaler.DriverManagementPage.line123_placeholder')" maxlength="18" />
        </el-form-item>
        <el-form-item label="车辆类型" prop="vehicleType">
          <el-input v-model="driverForm.vehicleType" :placeholder="$t('wholesaler.DriverManagementPage.line126_placeholder')" />
        </el-form-item>
        <el-form-item label="最大载重" prop="maxLoad">
          <el-input-number v-model="driverForm.maxLoad" :min="100" :max="1000" :step="50" /> kg
        </el-form-item>
        <el-form-item label="提成比例" prop="commissionRate">
          <el-input-number v-model="driverForm.commissionRate" :min="0" :max="100" :step="0.5" /> %
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="dialogVisible = false">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" @click="submitDriverForm" :loading="submitLoading">{{ $t('common.confirm') }}</el-button>
      </span>
    </el-dialog>

    <!-- 底部导航 -->
    <nav class="bottom-nav">
      <button class="nav-item" @click="navigateTo('home')">
        <span class="nav-item-icon">🏠</span>
        <span>{{ $t('common.home') }}</span>
      </button>
      <button class="nav-item" @click="navigateTo('products')">
        <span class="nav-item-icon">📦</span>
        <span>{{ $t('common.products') }}</span>
      </button>
      <button class="nav-item" @click="navigateTo('wholesale')">
        <span class="nav-item-icon">💰</span>
        <span>{{ $t('wholesaler.DriverManagementPage.line153_text') }}</span>
      </button>
      <button class="nav-item" @click="navigateTo('orders')">
        <span class="nav-item-icon">📋</span>
        <span>{{ $t('common.orders') }}</span>
      </button>
      <button class="nav-item" @click="navigateTo('profile')">
        <span class="nav-item-icon">👤</span>
        <span>{{ $t('common.profile') }}</span>
      </button>
    </nav>
  </div>
</template>

<script>
import { getDrivers, getDriverStats, createDriver, updateDriver, deleteDriver, updateDriverStatus, checkDriverPhone } from '@/api/delivery'
import { getWholesalerId } from '@/utils/wholesaler'

export default {
  name: 'DriverManagementMobile',
  data() {
    return {
      currentDate: '',
      loading: false,
      dialogVisible: false,
      dialogMode: 'add',
      submitLoading: false,
      searchKeyword: '',
      filterStatus: '',
      currentPage: 1,
      pageSize: 20,
      total: 0,
      drivers: [],
      stats: {},
      driverForm: {
        name: '',
        phone: '',
        idCard: '',
        vehicleType: '',
        maxLoad: 500,
        commissionRate: 5
      },
      formRules: {
        name: [{ required: true, message: '请输入姓名', trigger: 'blur' }],
        phone: [
          { required: true, message: '请输入手机号', trigger: 'blur' }
        ]
      }
    }
  },
  created() {
    console.log('🟠 DriverManagementPage created()')
    this.updateDate()
    this.loadStats()
    this.loadDrivers()
  },
  methods: {
    /**
     * 获取当前批发商 ID
     */
    getWholesalerId() {
      return getWholesalerId() ?? 1
    },
    
    updateDate() {
      const date = new Date()
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const week = ['日', '一', '二', '三', '四', '五', '六'][date.getDay()]
      this.currentDate = `${year}年${month}月${day}日 星期${week}`
    },
    
    async loadStats() {
      try {
        const res = await getDriverStats({ wholesalerId: this.getWholesalerId() })
        this.stats = res.data || res || {}
      } catch (error) {
        console.error('加载统计数据失败:', error)
        this.stats = {}
      }
    },
    
    async loadDrivers() {
      this.loading = true
      try {
        const params = {
          wholesalerId: this.getWholesalerId(),
          keyword: this.searchKeyword || undefined,
          page: this.currentPage - 1,
          size: this.pageSize
        }
        if (this.filterStatus) {
          params.status = this.filterStatus
        }
        
        console.log('🔵 请求参数:', params)
        const res = await getDrivers(params)
        console.log('🔴 配送员列表响应:', res)
        const allDrivers = res.content || res.data?.content || []
        console.log('🔴 解析后的配送员:', allDrivers)
        console.log('🔴 配送员数量:', allDrivers.length)
        
        this.drivers = allDrivers
        this.total = res.total || res.data?.total || 0
        console.log('🔴 drivers:', this.drivers)
        console.log('🔴 drivers.length:', this.drivers.length)
      } catch (error) {
        console.error('❌ 加载配送员列表失败:', error)
        console.error('❌ 错误详情:', error.response?.data)
        this.$message.error('加载配送员列表失败')
      } finally {
        this.loading = false
      }
    },
    
    showAddDialog() {
      console.log('🔴 点击了添加配送员按钮')
      this.dialogMode = 'add'
      this.driverForm = {
        name: '',
        phone: '',
        idCard: '',
        vehicleType: '',
        maxLoad: 500,
        commissionRate: 5
      }
      console.log('🔴 dialogVisible 前:', this.dialogVisible)
      this.dialogVisible = true
      console.log('🔴 dialogVisible 后:', this.dialogVisible)
      this.$nextTick(() => {
        console.log('🔴 nextTick 后执行')
        this.$refs.driverForm?.clearValidate()
      })
    },
    
    editDriver(driver) {
      this.dialogMode = 'edit'
      this.driverForm = { ...driver }
      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.driverForm?.clearValidate()
      })
    },
    
    async submitDriverForm() {
      try {
        await this.$refs.driverForm.validate()
        this.submitLoading = true
        
        if (this.dialogMode === 'add') {
          // 先检查手机号是否已存在
          try {
            const checkRes = await checkDriverPhone(this.driverForm.phone)
            if (checkRes.data && checkRes.data.exists) {
              this.$confirm('该手机号已存在，是否要编辑该配送员？', '提示', {
                confirmButtonText: '去编辑',
                cancelButtonText: '取消',
                type: 'warning'
              }).then(() => {
                // 加载该配送员信息进行编辑
                this.editDriverById(checkRes.data.driverId)
              }).catch(() => {})
              return
            }
          } catch (checkError) {
            // 如果检查接口报错，继续尝试创建（可能是后端没有这个接口）
            console.log('手机号检查跳过:', checkError)
          }
          
          // 设置批发商 ID
          this.driverForm.wholesalerId = this.getWholesalerId()
          console.log('🔴 提交配送员数据:', JSON.stringify(this.driverForm))
          
          await createDriver(this.driverForm)
          this.$message.success('添加成功')
        } else {
          await updateDriver(this.driverForm.id, this.driverForm)
          this.$message.success('更新成功')
        }
        
        this.dialogVisible = false
        this.loadDrivers()
        this.loadStats()
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('操作失败：' + (error.response?.data?.message || error.message))
        }
      } finally {
        this.submitLoading = false
      }
    },
    
    async toggleDriverStatus(driver) {
      try {
        const newStatus = driver.status === 'IDLE' ? 'OFFLINE' : 'IDLE'
        await updateDriverStatus(driver.id, { status: newStatus })
        this.$message.success('状态已更新')
        this.loadDrivers()
        this.loadStats()
      } catch (error) {
        this.$message.error('更新状态失败')
      }
    },
    
    assignOrder(driver) {
      this.$message.info('分配订单功能开发中...')
    },
    
    getStatusType(status) {
      const typeMap = {
        'IDLE': 'success',
        'DELIVERING': 'primary',
        'REST': 'warning',
        'OFFLINE': 'info'
      }
      return typeMap[status] || 'info'
    },
    
    getStatusText(status) {
      const textMap = {
        'IDLE': '空闲',
        'DELIVERING': '配送中',
        'REST': '休息',
        'OFFLINE': '离线'
      }
      return textMap[status] || status
    },

    navigateTo(tab) {
      const routes = {
        home: '/wholesaler/home',
        products: '/wholesaler/product',
        wholesale: '/wholesaler/wholesale-price-list',
        orders: '/wholesaler/orders',
        profile: '/my-profile'
      }
      if (routes[tab]) {
        this.$router.push(routes[tab])
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.driver-management-mobile {
  min-height: 100vh;
  background: #f5f7fa;
  padding: 16px;
  padding-bottom: 70px;
}

/* 底部导航 */
.bottom-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: space-around;
  z-index: 100;
  height: 56px;
}

.nav-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #999;
  font-size: 11px;
  border: none;
  background: none;
  transition: color 0.3s;
}

.nav-item.active {
  color: #667eea;
}

.nav-item-icon {
  font-size: 24px;
  margin-bottom: 2px;
}

// ⭐ 强制弹窗可见性修复
::v-deep .el-dialog {
  opacity: 1 !important;
  visibility: visible !important;
  z-index: 2000 !important;
  border-radius: 12px !important;
  max-width: 90% !important;
  width: 500px !important;
}

::v-deep .el-dialog__wrapper {
  opacity: 1 !important;
  visibility: visible !important;
  z-index: 2000 !important;
}

::v-deep .el-dialog__header {
  padding: 16px 20px !important;
  border-bottom: 1px solid #e0e0e0 !important;
}

::v-deep .el-dialog__title {
  font-size: 18px !important;
  font-weight: 600 !important;
}

::v-deep .el-dialog__body {
  padding: 20px !important;
  max-height: 60vh !important;
  overflow-y: auto !important;
}

::v-deep .el-dialog__footer {
  padding: 12px 20px !important;
  border-top: 1px solid #e0e0e0 !important;
}

// ⭐ 强制按钮可点击
::v-deep .el-button {
  pointer-events: auto !important;
  z-index: 1000 !important;
}

// ⭐ 表单样式优化
::v-deep .el-form-item {
  margin-bottom: 16px !important;
}

::v-deep .el-form-item__label {
  font-weight: 500 !important;
  color: #2c3e50 !important;
}

::v-deep .el-input__inner,
::v-deep .el-select__input {
  border-radius: 6px !important;
  border-color: #d0d7de !important;
}

::v-deep .el-input__inner:focus,
::v-deep .el-select__input:focus {
  border-color: #667eea !important;
  box-shadow: 0 0 0 3px rgba(102,126,234,0.1) !important;
}

.stats-section {
  margin-bottom: 16px;
  
  .stats-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    
    h2 {
      font-size: 18px;
      font-weight: 600;
      color: #2c3e50;
      margin: 0;
    }
    
    .date {
      font-size: 12px;
      color: #999;
    }
  }
  
  .stats-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 8px;
    
    .stat-card {
      background: white;
      border-radius: 8px;
      padding: 12px;
      text-align: center;
      box-shadow: 0 2px 8px rgba(0,0,0,0.05);
      
      .stat-value {
        font-size: 20px;
        font-weight: bold;
        margin-bottom: 4px;
        
        &.primary { color: #667eea; }
        &.success { color: #4caf50; }
        &.warning { color: #ff9800; }
        &.info { color: #2196f3; }
      }
      
      .stat-label {
        font-size: 11px;
        color: #999;
      }
    }
  }
}

.search-section {
  margin-bottom: 16px;
  
  .search-bar {
    margin-bottom: 8px;
  }
  
  .filter-select {
    width: 100%;
  }
}

.driver-list {
  .driver-card {
    background: white;
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 12px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
    
    .driver-header {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 16px;
      
      .driver-avatar {
        width: 48px;
        height: 48px;
        border-radius: 50%;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 20px;
        font-weight: bold;
        flex-shrink: 0;
      }
      
      .driver-info {
        flex: 1;
        min-width: 0;
        
        .driver-name {
          font-size: 16px;
          font-weight: 600;
          color: #2c3e50;
          margin-bottom: 4px;
        }
        
        .driver-phone {
          font-size: 13px;
          color: #666;
          margin-bottom: 2px;
        }
        
        .driver-vehicle {
          font-size: 12px;
          color: #999;
        }
      }
    }
    
    .driver-stats {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 12px;
      padding: 12px 0;
      border-top: 1px solid #f0f0f0;
      border-bottom: 1px solid #f0f0f0;
      margin-bottom: 12px;
      
      .driver-stat {
        text-align: center;
        
        .stat-value {
          font-size: 16px;
          font-weight: bold;
          color: #667eea;
          margin-bottom: 4px;
        }
        
        .stat-label {
          font-size: 11px;
          color: #999;
        }
      }
    }
    
    .driver-actions {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 8px;
    }
  }
  
  .loading-state, .empty-state {
    text-align: center;
    padding: 60px 20px;
    color: #999;
    
    .loading-icon, .empty-icon {
      font-size: 64px;
      margin-bottom: 16px;
    }
    
    p {
      margin-bottom: 20px;
    }
  }
}
</style>
